<script setup lang="ts">
import { ROLE_TYPE_USER } from '@/shared/constants/roleTypeConstants';
import { useModalStore } from '@/shared/ui/AppModal/store/modal';
import { useUserStore } from '@/entities/user/store';
import LawyerCardShareList from '@/widgets/LawyerCard/ui/LawyerCardShareList.vue';
import type {
  ILawyerCardContextContentProps,
  ILawyerCardContextContentEmits,
} from '@/widgets/LawyerCard/model/types/lawyerCardContext';

const props = withDefaults(defineProps<ILawyerCardContextContentProps>(), {
  isFeedback: false,
  isEdit: false,
  isLawyerCard: false,
});

const emit = defineEmits<ILawyerCardContextContentEmits>();

const modalStore = useModalStore();
const userStore = useUserStore();

const isShowCommentField = !props.isLawyerCard
  ? inject<Ref<boolean>>('isShowCommentField')
  : null;
const isShowContext = inject<Ref<boolean>>('isShowContext');

const isShare: Ref<boolean> = ref(false);
const { isAuth } = storeToRefs(userStore);

const buttonText: ComputedRef<string> = computed(() => {
  if (props.isFeedback && !props.isEdit) return 'Ответить';
  if (props.isFeedback && props.isEdit) return 'Редактировать';
  return 'Поделиться';
});

const complainHandler = (): void => {
  emit('show');
};

const copyHandler = (): void => {
  emit('copy');
};

const clickHandler = (): void => {
  if (!props.isFeedback && !props.isEdit) {
    isShare.value = true;
    return;
  }

  if (isAuth.value) {
    if (isShowCommentField) isShowCommentField.value = true;
    if (isShowContext) isShowContext.value = false;
    return;
  }

  if (isShowContext) isShowContext.value = false;
  modalStore.setModalState('auth', true, {
    isLogin: true,
    mode: ROLE_TYPE_USER,
  });
};
</script>

<template>
  <div class="context-modal-content">
    <div class="context-modal-content__content">
      <template v-if="!isShare">
        <button
          class="context-modal-content__button"
          aria-label="Поделиться"
          @click.prevent="clickHandler"
        >
          <AppIcon name="Share" />
          <span class="context-modal-content__button-text">{{ buttonText }}</span>
        </button>
        <button
          class="context-modal-content__button"
          aria-label="Пожаловаться"
          @click.prevent="complainHandler"
        >
          <AppIcon name="Alarm" />
          <span class="context-modal-content__button-text _danger">Пожаловаться</span>
        </button>
      </template>
      <template v-else>
        <LawyerCardShareList @copy="copyHandler" />
      </template>
    </div>
  </div>
</template>

<style lang="less">
.context-modal-content {
  min-width: 162px;
  background: #fff;
  border-radius: @main-size;
  box-shadow:
    -@main-size -5px 25px rgba(0, 0, 0, 0.06),
    0px @size-x10 @size-x10 -18px rgba(139, 144, 164, 0.2);
  padding: @size-x4 @size-x4 @size-x4 @size-x3;

  &._hide {
    min-width: 180px;
  }

  &__close {
    position: absolute;
    right: 14px;
    top: 14px;
    width: 14px;
    height: 14px;
    z-index: 3;

    path {
      stroke: @color-elephant;
    }
  }

  &__button {
    display: inline-flex;
    align-items: center;
    font-size: 14px;
    line-height: 19px;
    font-weight: 600;

    &:first-child {
      padding-bottom: @size-x3;
      margin-bottom: @size-x2;
      min-width: 117px;
      border-bottom: 0.25px solid @color-dove-gray;
    }

    svg {
      width: 13px;
      height: 13px;
    }
  }

  &__button-text {
    margin-left: @size-x2;

    &._danger {
      color: @color-crimson;
    }
  }
}
</style>
